/**
 * DeclaracoesEpiPanel
 * O painel "Declarações de EPI" da emissão do LTCAT (#468): por setor, o que cada cargo que
 * recebe EPI respondeu às cinco declarações do laudo.
 *
 * Carrega só com alcance e período escolhidos. As escolhas ficam num rascunho local e só vão ao
 * servidor no botão "Salvar respostas", que manda apenas as células alteradas, cada uma com o
 * valor que o painel viu. Se outra pessoa respondeu no meio, o servidor recusa (409), nada é
 * gravado e o painel mostra o que mudou.
 *
 * A resposta é do cargo e vale para todo laudo que o inclui, de qualquer alcance: o aviso fixo
 * do topo diz isso, porque quem responde aqui está emitindo UM laudo.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { OpenInNew as AbreEmOutraAbaIcon, Save as SalvarIcon } from '@mui/icons-material';
import { ActionButton } from '../../../components/common';
import { useAcao } from '../../../authorization/useVeredito';
import ltcatEpiDeclaracoesService, {
  type ConsultaDeclaracoesEpi,
} from '../../../services/ltcatEpiDeclaracoesService';
import type { LtcatEpiDeclaracaoChave, LtcatEpiDeclaracoesPainel } from '../../../types/document';
import { useAvisoDeSaida } from '../hooks/useAvisoDeSaida';
import { DeclaracoesEpiSetor } from './DeclaracoesEpiSetor';
import {
  CHAVES_DAS_DECLARACOES,
  ID_DO_PAINEL_DE_DECLARACOES_EPI,
  aplicarConflitos,
  alteracoesDoRascunho,
  chaveDaCelula,
  conflitosDoErro,
  escolher,
  reconciliarRascunho,
  respostasDoPainel,
  rotuloDaResposta,
  type MapaDeRespostas,
  type RespostaDaCelula,
} from './declaracoesEpi';

const PERMISSAO_DE_RESPONDER = 'document_emission.generate';

export const AVISO_DE_ALCANCE_DA_RESPOSTA =
  'A resposta vale para todo laudo que inclui este cargo, de qualquer alcance. Alterar uma resposta muda o conteúdo desses laudos, e a próxima emissão de cada um abre uma revisão nova.';
export const MOTIVO_DE_SOMENTE_LEITURA =
  'Seu perfil não pode responder as declarações de EPI: você só consulta as respostas. Peça a quem emite laudos para respondê-las.';
const SEM_ALCANCE = 'Escolha o alcance e o período para responder as declarações de EPI.';
const CARGOS_PARA_MOSTRAR_BUSCA = 6;
const SETORES_ABERTOS_DE_INICIO = 3;

type Aviso =
  | { tipo: 'salvo' }
  | { tipo: 'recarregado' }
  | { tipo: 'erro'; texto: string }
  | { tipo: 'conflito'; itens: string[] };

type Cargo = LtcatEpiDeclaracoesPainel['setores'][number]['cargos'][number];

const mensagemDeErro = (erro: unknown, fallback: string): string =>
  erro instanceof Error && erro.message !== '' ? erro.message : fallback;

const semAcento = (texto: string): string =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const quantasAlteracoes = (total: number): string =>
  total === 1 ? '1 resposta alterada' : `${total} respostas alteradas`;

export interface DeclaracoesEpiPanelProps {
  /** Alcance e período escolhidos; `null` enquanto falta algum. */
  consulta: ConsultaDeclaracoesEpi | null;
  /** Avisado quando a pessoa abre um link para outra aba — a emissão confere de novo na volta. */
  onAbrirLink?: () => void;
  /** Avisado depois de gravar: a prévia e as pendências mostradas deixaram de valer. */
  onSalvo?: () => void;
}

export const DeclaracoesEpiPanel: React.FC<DeclaracoesEpiPanelProps> = ({ consulta, onAbrirLink, onSalvo }) => {
  const [painel, setPainel] = useState<LtcatEpiDeclaracoesPainel | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erroDeCarga, setErroDeCarga] = useState<string | null>(null);
  const [recarregamento, setRecarregamento] = useState(0);
  const [rascunho, setRascunho] = useState<MapaDeRespostas>({});
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const painelRef = useRef<LtcatEpiDeclaracoesPainel | null>(null);
  // A consulta a que o painel mostrado pertence: painel de outra consulta não pode ficar na tela.
  const consultaDoPainelRef = useRef<string | null>(null);
  const avisarRecargaRef = useRef(false);

  const responder = useAcao(PERMISSAO_DE_RESPONDER, 'Responder as declarações de EPI');
  const somenteLeitura = responder.disabled;

  const chaveDaConsulta =
    consulta === null
      ? null
      : `${consulta.scope}|${consulta.scopeId}|${consulta.periodStart}|${consulta.periodEnd}`;

  useEffect(() => {
    painelRef.current = painel;
  }, [painel]);

  useEffect(() => {
    if (consulta === null) {
      setPainel(null);
      setRascunho({});
      setErroDeCarga(null);
      consultaDoPainelRef.current = null;
      return;
    }

    let ativo = true;
    setCarregando(true);
    setErroDeCarga(null);

    // Alcance ou período novo: o painel e o rascunho velhos pertencem à consulta anterior, e salvar
    // um com a consulta nova seria recusado (422). Só a recarga da MESMA consulta preserva o rascunho.
    if (consultaDoPainelRef.current !== chaveDaConsulta) {
      painelRef.current = null;
      setPainel(null);
      setRascunho({});
      setAviso(null);
    }

    ltcatEpiDeclaracoesService
      .obter(consulta)
      .then((novo) => {
        if (!ativo) return;
        const anterior = painelRef.current;
        setRascunho((atual) => (anterior === null ? {} : reconciliarRascunho(anterior, novo, atual)));
        if (anterior === null) {
          setAbertos(
            new Set(
              novo.setores.length <= SETORES_ABERTOS_DE_INICIO
                ? novo.setores.map((setor) => String(setor.departmentId))
                : []
            )
          );
        }
        consultaDoPainelRef.current = chaveDaConsulta;
        setPainel(novo);
        setAviso(avisarRecargaRef.current ? { tipo: 'recarregado' } : null);
        avisarRecargaRef.current = false;
      })
      .catch((erro: unknown) => {
        if (!ativo) return;
        setErroDeCarga(mensagemDeErro(erro, 'Não foi possível carregar as declarações de EPI.'));
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
    // A consulta entra pela chave: o objeto novo com os mesmos valores não pede nova carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDaConsulta, recarregamento]);

  const carregadas = useMemo(() => (painel === null ? {} : respostasDoPainel(painel)), [painel]);
  const totalAlterado = Object.keys(rascunho).length;
  useAvisoDeSaida(totalAlterado > 0);

  const escolherResposta = useCallback(
    (occupationId: number | string, chave: LtcatEpiDeclaracaoChave, resposta: RespostaDaCelula): void => {
      setRascunho((atual) => escolher(atual, carregadas, chaveDaCelula(occupationId, chave), resposta));
      setAviso(null);
    },
    [carregadas]
  );

  const aplicarATodos = useCallback(
    (
      setor: LtcatEpiDeclaracoesPainel['setores'][number],
      chave: LtcatEpiDeclaracaoChave,
      resposta: RespostaDaCelula
    ): void => {
      setRascunho((atual) =>
        setor.cargos.reduce(
          (proximo, cargo) => escolher(proximo, carregadas, chaveDaCelula(cargo.occupationId, chave), resposta),
          atual
        )
      );
      setAviso(null);
    },
    [carregadas]
  );

  const salvar = async (): Promise<void> => {
    if (consulta === null || painel === null || totalAlterado === 0) return;
    const alteracoes = alteracoesDoRascunho(painel, rascunho);

    setSalvando(true);
    setAviso(null);
    try {
      const atualizado = await ltcatEpiDeclaracoesService.salvar(consulta, alteracoes);
      setPainel(atualizado);
      setRascunho({});
      setAviso({ tipo: 'salvo' });
      onSalvo?.();
    } catch (erro: unknown) {
      const conflitos = conflitosDoErro(erro);
      if (conflitos === null) {
        setAviso({ tipo: 'erro', texto: mensagemDeErro(erro, 'Não foi possível salvar as respostas.') });
        return;
      }

      // Conflito sem células (disputa que o servidor não soube apontar): não há o que listar, então
      // o painel é lido de novo e as escolhas ainda válidas são mantidas.
      if (conflitos.length === 0) {
        avisarRecargaRef.current = true;
        setRecarregamento((n) => n + 1);
        return;
      }

      const nomes = new Map<string, { cargo: string; setor: string }>();
      painel.setores.forEach((setor) =>
        setor.cargos.forEach((cargo) =>
          nomes.set(String(cargo.occupationId), { cargo: cargo.occupationName, setor: setor.departmentName })
        )
      );
      const itens = conflitos.map((conflito) => {
        const celula = chaveDaCelula(conflito.occupationId, conflito.declarationKey);
        const nome = nomes.get(String(conflito.occupationId));
        const numero = CHAVES_DAS_DECLARACOES.indexOf(conflito.declarationKey) + 1;
        return `${nome?.cargo ?? 'Cargo'} (${nome?.setor ?? 'setor'}), declaração ${numero}: agora está ${rotuloDaResposta(conflito.atual)}; você tinha escolhido ${rotuloDaResposta(rascunho[celula] ?? null)}.`;
      });
      setPainel(aplicarConflitos(painel, conflitos));
      setRascunho((atual) => {
        const restante = { ...atual };
        conflitos.forEach((conflito) => delete restante[chaveDaCelula(conflito.occupationId, conflito.declarationKey)]);
        return restante;
      });
      setAviso({ tipo: 'conflito', itens });
    } finally {
      setSalvando(false);
    }
  };

  const termo = semAcento(busca.trim());
  const totalDeCargos = painel?.setores.reduce((total, setor) => total + setor.cargos.length, 0) ?? 0;
  const setoresVisiveis = useMemo(() => {
    if (painel === null) return [];
    return painel.setores
      .map((setor) => {
        const cargos: Cargo[] =
          termo === '' || semAcento(setor.departmentName).includes(termo)
            ? setor.cargos
            : setor.cargos.filter((cargo) => semAcento(cargo.occupationName).includes(termo));
        return { setor, cargos };
      })
      .filter(({ cargos }) => cargos.length > 0);
  }, [painel, termo]);

  const alternarSetor = (departmentId: number | string): void =>
    setAbertos((atuais) => {
      const proximo = new Set(atuais);
      const id = String(departmentId);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  const linkParaOutraAba = (rota: string, rotulo: string): React.ReactNode => (
    <Link
      href={rota}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      title="Abre em outra aba"
      onClick={onAbrirLink}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {rotulo}
      <AbreEmOutraAbaIcon sx={{ fontSize: 14 }} />
    </Link>
  );

  // Painel só aparece inteiro e sem erro: durante a carga ou depois de falha ele não deve ser editado.
  const painelVisivel = painel !== null && erroDeCarga === null;
  const semCargos = painelVisivel && painel.setores.length === 0;

  return (
    <Box id={ID_DO_PAINEL_DE_DECLARACOES_EPI} tabIndex={-1} component="section" aria-labelledby="titulo-declaracoes-epi" sx={{ outline: 'none' }}>
      <Typography id="titulo-declaracoes-epi" variant="subtitle1" component="h3">
        Declarações de EPI
      </Typography>

      {consulta === null && (
        <Typography variant="body2" color="text.secondary">
          {SEM_ALCANCE}
        </Typography>
      )}

      {consulta !== null && carregando && !painelVisivel && erroDeCarga === null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={20} aria-label="Carregando as declarações de EPI" />
          <Typography variant="body2">Carregando as declarações de EPI…</Typography>
        </Box>
      )}

      {consulta !== null && erroDeCarga !== null && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => setRecarregamento((n) => n + 1)}>
              Tentar de novo
            </Button>
          }
        >
          {erroDeCarga}
        </Alert>
      )}

      {consulta !== null && painelVisivel && (
        <>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            {AVISO_DE_ALCANCE_DA_RESPOSTA}
          </Alert>

          {somenteLeitura && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {MOTIVO_DE_SOMENTE_LEITURA}
            </Alert>
          )}

          {painel.funcionarioSemCargoComEpi && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Há funcionário com EPI informado que está sem cargo, e o EPI dele não tem cargo para responder.
              Cadastre o cargo do funcionário: {linkParaOutraAba('/employees', 'Abrir funcionários')}
            </Alert>
          )}

          {semCargos && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {painel.funcionarioSemCargoComEpi
                ? 'Nenhum cargo deste alcance recebe EPI. Até o cargo do funcionário ser cadastrado, as cinco declarações saem Pendente.'
                : 'Nenhum cargo deste alcance recebe EPI; as cinco declarações saem Não aplicável. Se isto não confere, o EPI é informado no cadastro de riscos: '}
              {!painel.funcionarioSemCargoComEpi && linkParaOutraAba('/risk-applications', 'Abrir o cadastro de riscos')}
            </Alert>
          )}

          {!semCargos && (
            <>
              <Box component="ol" sx={{ pl: 3, mt: 0, mb: 2 }}>
                {painel.declaracoes.map((declaracao) => (
                  <Typography component="li" variant="body2" key={declaracao.chave} sx={{ mb: 0.5 }}>
                    {declaracao.texto}
                  </Typography>
                ))}
              </Box>

              {totalDeCargos > CARGOS_PARA_MOSTRAR_BUSCA && (
                <TextField
                  size="small"
                  fullWidth
                  label="Buscar cargo ou setor"
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {setoresVisiveis.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Nenhum cargo com este nome.
                </Typography>
              )}

              {setoresVisiveis.map(({ setor, cargos }) => (
                <DeclaracoesEpiSetor
                  key={String(setor.departmentId)}
                  setor={setor}
                  cargosVisiveis={cargos}
                  carregadas={carregadas}
                  rascunho={rascunho}
                  somenteLeitura={somenteLeitura || salvando || carregando}
                  aberto={termo !== '' || abertos.has(String(setor.departmentId))}
                  onAlternar={() => alternarSetor(setor.departmentId)}
                  onEscolher={escolherResposta}
                  onAplicarATodos={aplicarATodos}
                />
              ))}

              {aviso?.tipo === 'conflito' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Outra pessoa respondeu antes de você. Nada foi salvo.</AlertTitle>
                  As respostas abaixo foram atualizadas na tela; confira e salve de novo o que ainda vale.
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {aviso.itens.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Box>
                </Alert>
              )}
              {aviso?.tipo === 'recarregado' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Outra pessoa mexeu nas respostas ao mesmo tempo. Nada foi salvo; o painel foi atualizado.
                  Confira e salve de novo o que ainda vale.
                </Alert>
              )}
              {aviso?.tipo === 'erro' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {aviso.texto} Suas alterações continuam na tela.
                </Alert>
              )}
              {aviso?.tipo === 'salvo' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Respostas salvas.
                </Alert>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  position: 'sticky',
                  bottom: 0,
                  py: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <ActionButton
                  acao={PERMISSAO_DE_RESPONDER}
                  rotulo="Salvar respostas"
                  variant="contained"
                  startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <SalvarIcon />}
                  disabled={totalAlterado === 0 || salvando || carregando}
                  onClick={() => void salvar()}
                >
                  {salvando ? 'Salvando…' : 'Salvar respostas'}
                </ActionButton>
                <Typography variant="body2" color={totalAlterado > 0 ? 'text.primary' : 'text.secondary'}>
                  {totalAlterado > 0
                    ? `${quantasAlteracoes(totalAlterado)}, ainda não salvas — o laudo só usa o que está salvo.`
                    : 'Nenhuma alteração por salvar.'}
                </Typography>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default DeclaracoesEpiPanel;
